import { AnimatePresence, motion } from "framer-motion"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { ArrowIcon, BedIcon, ChevronIcon, PinIcon, PlaneIcon, StarIcon, UsersIcon } from "./Icons"

const spring = { type: "spring", stiffness: 260, damping: 22 }

const Fact = ({ icon, children }) => (
  <div className="flex items-center gap-1.5 text-[13px] text-aichat-ink-muted">
    <span className="shrink-0">{icon}</span>
    <span className="truncate">{children}</span>
  </div>
)

// Встроенная заглушка: используется, если сайт не передал свою (noPhotoUrl) или она не загрузилась
const DEFAULT_NO_PHOTO =
  "data:image/svg+xml;charset=utf-8," +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 150"><rect width="400" height="150" fill="#eef0f3"/>' +
      '<g fill="none" stroke="#b5bcc6" stroke-width="4" stroke-linejoin="round" transform="translate(170 45)">' +
      '<rect x="2" y="2" width="56" height="56" rx="8"/><circle cx="20" cy="20" r="6"/><path d="M2 46l16-16 12 12 10-8 18 16"/></g></svg>'
  )

const HotelCard = ({ hotel, onOpen, noPhotoUrl }) => {
  const { t } = useTranslation()
  const fallback = noPhotoUrl || DEFAULT_NO_PHOTO
  const [src, setSrc] = useState(hotel.image || fallback)

  // фото отеля битое → заглушка сайта; заглушка сайта битая → встроенная
  const handleError = () =>
    setSrc((current) =>
      current === DEFAULT_NO_PHOTO ? current : current === fallback ? DEFAULT_NO_PHOTO : fallback
    )

  return (
    <div className="overflow-hidden rounded-card border border-aichat-line bg-aichat-surface shadow-md">
      <div className="relative h-[150px] overflow-hidden bg-aichat-surface-soft">
        {src && (
          <motion.img
            src={src}
            onError={handleError}
            alt={hotel.name}
            loading="lazy"
            initial={{ scale: 1.15 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="h-full w-full object-cover"
          />
        )}
        {hotel.badge && (
          <motion.span
            initial={{ opacity: 0, y: -10, scale: 0.8 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ delay: 0.25, ...spring }}
            className="absolute right-2 top-2 flex items-center gap-1 rounded-badge bg-aichat-surface px-2.5 py-1 text-xs font-medium text-aichat-ink shadow"
          >
            <StarIcon className="text-aichat-accent" width={14} height={14} />
            {hotel.badge}
          </motion.span>
        )}
      </div>

      <div className="space-y-2 p-3">
        {!!hotel.stars && (
          <div className="flex text-aichat-accent">
            {Array.from({ length: hotel.stars }).map((_, i) => (
              <motion.span
                key={i}
                initial={{ scale: 0, rotate: -40 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ delay: 0.15 + i * 0.06, type: "spring", stiffness: 400, damping: 14 }}
                className="flex"
              >
                <StarIcon />
              </motion.span>
            ))}
          </div>
        )}

        <div className="flex items-start justify-between gap-2">
          <h4 className="text-base font-bold leading-tight text-aichat-ink">{hotel.name}</h4>
          <motion.div
            initial={{ opacity: 0, x: 10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2, ...spring }}
            className="shrink-0 text-right"
          >
            <div className="text-lg font-bold leading-tight text-aichat-primary">
              {hotel.currency === "EUR" || !hotel.currency ? "€" : `${hotel.currency} `}
              {hotel.price}
            </div>
            {!!hotel.nights && (
              <div className="text-xs text-aichat-ink-muted">
                / {hotel.nights} {t("ai_chat.nights", "nights")}
              </div>
            )}
          </motion.div>
        </div>

        <motion.div
          initial="hidden"
          animate="show"
          variants={{
            hidden: {},
            show: { transition: { delayChildren: 0.25, staggerChildren: 0.06 } },
          }}
          className="grid grid-cols-2 gap-x-2 gap-y-1"
        >
          {[
            hotel.location && (
              <Fact key="loc" icon={<PinIcon />}>
                {hotel.location}
              </Fact>
            ),
            hotel.board && (
              <Fact key="board" icon={<BedIcon />}>
                {hotel.board}
              </Fact>
            ),
            !!hotel.persons && (
              <Fact key="pers" icon={<UsersIcon />}>
                {hotel.persons} {t("ai_chat.persons", "pers.")}
              </Fact>
            ),
            hotel.transfer && (
              <Fact key="tr" icon={<PlaneIcon width={16} height={16} />}>
                {t("ai_chat.transfer", "Transfer included")}
              </Fact>
            ),
          ]
            .filter(Boolean)
            .map((fact) => (
              <motion.div
                key={fact.key}
                variants={{ hidden: { opacity: 0, y: 6 }, show: { opacity: 1, y: 0 } }}
              >
                {fact}
              </motion.div>
            ))}
        </motion.div>

        <motion.button
          type="button"
          onClick={() => onOpen?.(hotel)}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.97 }}
          className="group flex w-full items-center justify-center gap-2 rounded-button bg-aichat-primary py-2.5 text-sm font-semibold text-aichat-on-primary transition-colors hover:bg-aichat-primary-strong"
        >
          {hotel.url
            ? t("ai_chat.book", "Book")
            : t("ai_chat.request_offer", "Request offer")}
          <span className="flex transition-transform group-hover:translate-x-1">
            <ArrowIcon />
          </span>
        </motion.button>
      </div>
    </div>
  )
}

const slide = {
  enter: (dir) => ({ x: dir * 60, opacity: 0 }),
  center: { x: 0, opacity: 1 },
  exit: (dir) => ({ x: dir * -60, opacity: 0 }),
}

// onSelect(hotel) вызывается для карточек без url (публичный чат: клиент просит предложение)
const HotelCards = ({ hotels, onSelect, noPhotoUrl }) => {
  const [[index, dir], setPage] = useState([0, 1])
  if (!hotels?.length) return null
  const hotel = hotels[index]
  const many = hotels.length > 1

  const go = (next, d) => setPage([(next + hotels.length) % hotels.length, d])
  const open = (h) => (h.url ? window.open(h.url, "_blank", "noopener") : onSelect?.(h))

  return (
    <motion.div
      initial={{ opacity: 0, y: 20, scale: 0.96 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ delay: 0.15, ...spring }}
      className="mt-2 w-full"
    >
      <div className="relative">
        <div className="overflow-hidden rounded-card">
          <AnimatePresence mode="wait" custom={dir} initial={false}>
            <motion.div
              key={hotel.id ?? index}
              custom={dir}
              variants={slide}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.2, ease: "easeOut" }}
            >
              <HotelCard hotel={hotel} onOpen={open} noPhotoUrl={noPhotoUrl} />
            </motion.div>
          </AnimatePresence>
        </div>

        {many && (
          <>
            <motion.button
              type="button"
              aria-label="prev"
              onClick={() => go(index - 1, -1)}
              whileHover={{ scale: 1.15 }}
              whileTap={{ scale: 0.85 }}
              className="absolute left-2 top-[67px] flex h-8 w-8 items-center justify-center rounded-full bg-aichat-surface/90 text-aichat-ink shadow"
            >
              <ChevronIcon dir="left" />
            </motion.button>
            <motion.button
              type="button"
              aria-label="next"
              onClick={() => go(index + 1, 1)}
              whileHover={{ scale: 1.15 }}
              whileTap={{ scale: 0.85 }}
              className="absolute right-2 top-[67px] flex h-8 w-8 items-center justify-center rounded-full bg-aichat-surface/90 text-aichat-ink shadow"
            >
              <ChevronIcon />
            </motion.button>
          </>
        )}
      </div>

      {many && (
        <div className="mt-2 flex items-center justify-center gap-1.5">
          {hotels.map((h, i) => (
            <motion.button
              key={h.id ?? i}
              type="button"
              aria-label={`hotel ${i + 1}`}
              onClick={() => go(i, i > index ? 1 : -1)}
              animate={{ width: i === index ? 20 : 8 }}
              transition={spring}
              className={`h-2 rounded-full transition-colors ${
                i === index ? "bg-aichat-primary" : "bg-aichat-primary/25"
              }`}
            />
          ))}
        </div>
      )}
    </motion.div>
  )
}

export default HotelCards
